import { api } from '@/lib/axios';

export interface FlowNode {
  id: string;
  type: string;
  position: { x: number; y: number };
  data: any;
}

export interface FlowEdge {
  id: string;
  source: string;
  target: string;
  type?: string;
}

export interface FlowData {
  nodes: FlowNode[];
  edges: FlowEdge[];
}

export type FlowType = 'MANUAL' | 'AUTO_SALE' | 'AUTO_RESERVATION' | 'AUTO_KEYWORD';

export interface Flow {
  id: string;
  name: string;
  description?: string;
  flowType: FlowType;
  flowData: string;
  listId?: string;
  venueId?: string;
  productId?: string;
  boardId?: string;
  initialStepId?: string;
  keywords?: string;
  allowedDays?: string;
  startTime?: string;
  endTime?: string;
  delayBetweenMessages: number;
  delayBetweenClients: number;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  list?: any;
  venue?: any;
  product?: any;
  board?: any;
  initialStep?: any;
  _count?: {
    executions: number;
  };
}

export interface FlowExecution {
  id: string;
  flowId: string;
  clientId: string;
  status: string;
  currentNodeId?: string;
  startedAt?: string;
  completedAt?: string;
  error?: string;
  executionData?: string;
  createdAt: string;
  updatedAt: string;
  flow?: Flow;
  client?: any;
  messages?: FlowMessage[];
}

export interface FlowMessage {
  id: string;
  executionId: string;
  nodeId: string;
  content: string;
  sentAt: string;
  delivered: boolean;
  read: boolean;
  error?: string;
}

export interface CreateFlowDto {
  name: string;
  description?: string;
  flowType?: FlowType;
  flowData: string;
  listId?: string | null;
  venueId?: string;
  productId?: string;
  boardId?: string;
  initialStepId?: string;
  keywords?: string;
  allowedDays?: string;
  startTime?: string;
  endTime?: string;
  delayBetweenMessages?: number;
  delayBetweenClients?: number;
  active?: boolean;
}

export interface UpdateFlowDto {
  name?: string;
  description?: string;
  flowType?: FlowType;
  flowData?: string;
  listId?: string | null;
  boardId?: string;
  initialStepId?: string;
  keywords?: string;
  allowedDays?: string;
  startTime?: string;
  endTime?: string;
  delayBetweenMessages?: number;
  delayBetweenClients?: number;
  active?: boolean;
}

export interface StartFlowDto {
  clientIds?: string[];
  clientPhones?: string[];
  listId?: string;
  whatsappConnectionId?: string;
}

export const flowsService = {
  async getAll(): Promise<Flow[]> {
    const response = await api.get<Flow[]>('/flows');
    return response.data;
  },

  async getById(id: string): Promise<Flow> {
    const response = await api.get<Flow>(`/flows/${id}`);
    return response.data;
  },

  async create(data: CreateFlowDto): Promise<Flow> {
    const response = await api.post<Flow>('/flows', data);
    return response.data;
  },

  async update(id: string, data: UpdateFlowDto): Promise<Flow> {
    const response = await api.patch<Flow>(`/flows/${id}`, data);
    return response.data;
  },

  async delete(id: string): Promise<void> {
    await api.delete(`/flows/${id}`);
  },

  async startFlow(
    id: string,
    data: StartFlowDto
  ): Promise<{ message: string; executions: FlowExecution[]; skipped: number }> {
    const response = await api.post(`/flows/${id}/start`, data);
    return response.data;
  },

  async getExecutions(params?: {
    flowId?: string;
    clientId?: string;
    status?: string;
    page?: number;
    limit?: number;
  }): Promise<{ data: FlowExecution[]; meta: any }> {
    const response = await api.get('/flows/executions/all', { params });
    return response.data;
  },

  async getExecutionById(id: string): Promise<FlowExecution> {
    const response = await api.get<FlowExecution>(`/flows/executions/${id}`);
    return response.data;
  },

  async pauseExecution(id: string): Promise<FlowExecution> {
    const response = await api.patch<FlowExecution>(`/flows/executions/${id}/pause`);
    return response.data;
  },

  async resumeExecution(id: string): Promise<FlowExecution> {
    const response = await api.patch<FlowExecution>(`/flows/executions/${id}/resume`);
    return response.data;
  },

  async cancelExecution(id: string): Promise<FlowExecution> {
    const response = await api.patch<FlowExecution>(`/flows/executions/${id}/cancel`);
    return response.data;
  },

  async retryExecution(id: string): Promise<FlowExecution> {
    const response = await api.patch<FlowExecution>(`/flows/executions/${id}/retry`);
    return response.data;
  },

  async duplicate(id: string): Promise<Flow> {
    const response = await api.post<Flow>(`/flows/${id}/duplicate`);
    return response.data;
  },
};
